import { Product } from "../pos"
import { ProductChoice } from "./evaluated-service-bike-part"
import { ServiceProposalPartAction } from "./fresh-service-proposal"
import { PartFunction } from "./part-function"
import { PartGrade } from "./part-grade"
import { ServiceAction, ServicePartActionUtils } from "./service-part-action"

const choiceAlternatives = (choice: ProductChoice): Product[] => [
  choice.recommendedAlternative,
  ...choice.otherAlternatives,
]

/** Whichever alternative the customer picks, it has to be ordered. */
export const choiceAlwaysNeedsOrdering = (choice: ProductChoice): boolean =>
  choiceAlternatives(choice).every((product) => product.needsOrdering)

/**
 * Some alternatives are on the shelf and some are not, so whether anything gets
 * ordered comes down to which one the customer picks.
 */
export const choiceMayNeedOrdering = (choice: ProductChoice): boolean => {
  const alternatives = choiceAlternatives(choice)
  return (
    alternatives.some((product) => product.needsOrdering) &&
    !alternatives.every((product) => product.needsOrdering)
  )
}

/**
 * Worse wear sorts first. "Montera" puts a component on the bike that is not
 * there yet, so it has no wear to report and nothing to say about urgency, and
 * an action that needs no grade may have none either: they rank past the worst real grade rather than being read as pristine. A finite
 * sentinel, so two of them subtract to 0 and fall through to the name instead
 * of handing the comparator a `NaN`.
 */
const NO_GRADE_RANK = PartGrade.TEN + 1

const getGradeRank = (action: ServiceProposalPartAction): number =>
  ServicePartActionUtils.addsNewComponent(action.action)
    ? NO_GRADE_RANK
    : (action.partGrade ?? NO_GRADE_RANK)

const FUNCTION_RANK: Record<PartFunction, number> = {
  [PartFunction.NOT_WORKING]: 0,
  [PartFunction.NOT_OPTIMAL]: 1,
  [PartFunction.WORKS]: 2,
}

/** "Byt" first; adjusting and checking last; everything else between. */
const getActionKindRank = (action: ServiceAction): number => {
  if (action === ServiceAction.CHANGE) return 0
  if (action === ServiceAction.ADJUST || action === ServiceAction.CHECK) {
    return 2
  }
  return 1
}

/**
 * Worst off first: a component that doesn't work, then the most worn, then a
 * replacement before an adjustment or a check. The name keeps the rest
 * deterministic.
 */
export const compareByCondition = (
  a: ServiceProposalPartAction,
  b: ServiceProposalPartAction,
): number =>
  FUNCTION_RANK[a.partFunction] - FUNCTION_RANK[b.partFunction] ||
  getGradeRank(a) - getGradeRank(b) ||
  getActionKindRank(a.action) - getActionKindRank(b.action) ||
  a.partName.localeCompare(b.partName, "sv")

/**
 * A mixed list with its part actions in `compareByCondition` order. Only the
 * part actions move, among the places they already hold, so packages, washes,
 * manual actions and fees stay where they are.
 */
export const withPartActionsByCondition = <T>(
  items: T[],
  getPartAction: (item: T) => ServiceProposalPartAction | null,
): T[] => {
  const sorted = items
    .filter((item) => getPartAction(item) !== null)
    .sort((a, b) => {
      const actionA = getPartAction(a)
      const actionB = getPartAction(b)
      return actionA && actionB ? compareByCondition(actionA, actionB) : 0
    })
  let next = 0
  return items.map((item) => {
    if (getPartAction(item) === null) return item
    const placed = sorted[next]
    next += 1
    return placed ?? item
  })
}
